/**
 * @fileoverview SectionRenderer — one section + its fields.
 * @module inspection-renderer/sections
 *
 * Responsibilities:
 * - Render a single `InspectionLayoutSection`: title, description, and the
 *   ordered list of fields it owns (delegated to `<FieldRenderer>`).
 * - Apply column clamping via `clampSectionColumns()` so the host's declared
 *   column count never exceeds the active density tier (B5 / D2).
 * - Surface the "hidden with data" banner when the section is hidden by rules
 *   but contains at least one persisted, non-empty response (G-006).
 * - Wrap the section in `<SectionErrorBoundary>` so a single broken widget
 *   does not blow up the whole form (G-005).
 * - Honor `collapsible` + `defaultCollapsed`.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `<FieldRenderer>` for per-field dispatch.
 * - `<SectionErrorBoundary>` for G-005.
 * - `clampSectionColumns()` + `getDensity()` for density tokens (D2).
 *
 * Architecture:
 * - Stateless apart from the local "collapsed" toggle.
 * - All persisted state lives in the host; this component is pass-through.
 *
 * @component SectionRenderer
 *
 * @contract SectionRendererProps
 * Purpose: prop contract for a single section + its fields renderer.
 */
import { useState, type ErrorInfo, type ReactNode } from 'react';
import type {
  InspectionFormField,
  InspectionLayoutSection,
  ResolvedFieldStateV2,
  ResolvedSectionState,
} from '@firesquirrel/core-domain';
import { FieldRenderer } from '../fields/FieldRenderer';
import { SectionErrorBoundary } from './SectionErrorBoundary';
import { clampSectionColumns, getDensity, type GetDensityOpts } from '../tokens/densityTokens';
import { isFieldAnswered, isRequiredEffective } from '../validation/fieldIssues';
import { resolveFieldValueForValidation } from '../validation/dataBoundFieldValue';
import { useResolveContext } from '../contracts/rendererContext';

export interface SectionRendererProps {
  /** Owning form id — required by inline violation capture to persist into the right form. */
  formId?: string;
  section: InspectionLayoutSection;
  /** Lookup table: fieldId → canonical field. Sections only carry `fieldIds`. */
  fieldsById: Record<string, InspectionFormField>;
  /** Lookup table: fieldId → current persisted value. */
  valuesById: Record<string, unknown>;
  /** Lookup table: fieldId → isNA flag. */
  naById?: Record<string, boolean>;
  /** Per-field resolved state from engine v2. */
  resolvedFields?: Record<string, ResolvedFieldStateV2>;
  /** Resolved state for this section itself (hidden flag). */
  resolvedSection?: ResolvedSectionState;
  /** Per-field validation errors. */
  errorsById?: Record<string, string>;
  /** Whether to show the conduct-mode required completion badge. */
  showRequiredProgress?: boolean;
  /** Bulk-pilot mode disables all controls (B14). */
  bulkPilotMode?: boolean;
  /** Lock only data-bound widgets while leaving ordinary fields editable. */
  dataBoundReadOnly?: boolean;
  /** Density preference forwarded to widgets via density tokens. */
  density?: GetDensityOpts;
  /** When true, the inspection is completed or under review; all controls are disabled. */
  readOnly?: boolean;
  requiredImmediate?: boolean;
  onFieldChange: (fieldId: string, next: unknown) => void;
  onToggleNA?: (fieldId: string, next: boolean) => void;
  onSectionError?: (err: Error, info: ErrorInfo, sectionId: string) => void;
  /** Forwarded to FieldRenderer — triggers default-value re-application after a session reset. */
  resetKey?: string | number;
}

function hasAnyData(values: Record<string, unknown>, fieldIds: string[]): boolean {
  return fieldIds.some((id) => {
    const v = values[id];
    if (v == null) return false;
    if (typeof v === 'string') return v.length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v as object).length > 0;
    return true;
  });
}

export function SectionRenderer(props: SectionRendererProps): ReactNode {
  const {
    formId,
    section, fieldsById, valuesById, naById,
    resolvedFields, resolvedSection, errorsById, showRequiredProgress,
    bulkPilotMode, readOnly, dataBoundReadOnly, density, requiredImmediate,
    onFieldChange, onToggleNA, onSectionError, resetKey,
  } = props;

  const [collapsed, setCollapsed] = useState<boolean>(!!section.defaultCollapsed);
  const resolveContext = useResolveContext();

  // G-006 — hidden-with-data banner.
  const isHidden = !!resolvedSection?.hidden;
  const hasData = hasAnyData(valuesById, section.fieldIds);

  if (isHidden && !hasData) return null;

  const densityTokens = getDensity(density);
  const columns = clampSectionColumns(section.columns ?? 1, densityTokens);

  // Section-level required-progress badge. Counts only currently-visible
  // fields (resolved.hidden filtered out) so dynamically hidden required
  // fields don't show as "missing" in the badge.
  let requiredCount = 0;
  let requiredAnswered = 0;
  for (const fid of section.fieldIds) {
    const field = fieldsById[fid];
    if (!field) continue;
    const r = resolvedFields?.[fid];
    if (r?.hidden) continue;
    if (!isRequiredEffective(field, r)) continue;
    requiredCount += 1;
    if (isFieldAnswered(
      field,
      resolveFieldValueForValidation(field, valuesById[fid], naById?.[fid], resolveContext ?? undefined),
      naById?.[fid],
    )) requiredAnswered += 1;
  }
  const requiredAllMet = requiredCount === 0 || requiredAnswered === requiredCount;
  const badgeClass = requiredAllMet
    ? 'bg-gray-100 text-gray-600'
    : 'bg-amber-100 text-amber-800';

  return (
    <SectionErrorBoundary
      sectionId={section.id}
      sectionTitle={section.title}
      onError={onSectionError}
    >
      <section
        data-section-id={section.id}
        data-section-collapsed={collapsed ? 'true' : 'false'}
        data-section-columns={columns}
        className="border border-gray-200 rounded-sm mb-3"
      >
        <header className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2 min-w-0">
            <div className="min-w-0">
              {section.title && (
                <h2 className="text-sm font-semibold text-gray-800">{section.title}</h2>
              )}
              {section.description && (
                <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
              )}
            </div>
            {showRequiredProgress && !readOnly && requiredCount > 0 && (
              <span
                role="status"
                data-section-required-badge="true"
                data-required-met={requiredAllMet ? 'true' : 'false'}
                aria-label={`${requiredAnswered} of ${requiredCount} required answered`}
                className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${badgeClass}`}
              >
                {requiredAnswered}/{requiredCount} required
              </span>
            )}
          </div>
          {section.collapsible && (
            <button
              type="button"
              aria-expanded={!collapsed}
              aria-controls={`section-body-${section.id}`}
              onClick={() => setCollapsed((v) => !v)}
              className="text-xs text-blue-600 hover:underline"
            >
              {collapsed ? 'Expand' : 'Collapse'}
            </button>
          )}
        </header>
        {isHidden && hasData && (
          <div
            role="note"
            data-banner="hidden-with-data"
            className="bg-amber-50 border-b border-amber-200 px-3 py-1.5 text-xs text-amber-800"
          >
            This section is hidden by rules, but contains saved responses. They will be preserved.
          </div>
        )}
        {!collapsed && (
          <div
            id={`section-body-${section.id}`}
            className={`p-3 grid ${densityTokens.rowGap}`}
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {section.fieldIds.map((fid, index) => {
              const field = fieldsById[fid];
              if (!field) return null;
              return (
                <FieldRenderer
                  key={`${section.id}:${field.id || fid}:${index}`}
                  field={field}
                  value={valuesById[field.id]}
                  isNA={naById?.[field.id]}
                  resolved={resolvedFields?.[field.id]}
                  formId={formId}
                  errorMessage={errorsById?.[field.id]}
                  bulkPilotMode={bulkPilotMode}
                  readOnly={readOnly}
                  requiredImmediate={requiredImmediate}
                  dataBoundReadOnly={dataBoundReadOnly}
                  onFieldChange={onFieldChange}
                  onToggleNA={onToggleNA}
                  resetKey={resetKey}
                />
              );
            })}
          </div>
        )}
      </section>
    </SectionErrorBoundary>
  );
}
